import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { seoAPI } from '../../../config/seo-api';
import { helpers } from '../../../utils/helpers';
import {
  getCategorySeoScore,
  getCategorySeoStatus,
  getSeoStatusClass,
  SEO_SCORE_FILTER_OPTIONS,
} from '../../../utils/categorySeoHelpers';
import EditCategorySeoModal from './EditCategorySeoModal';
import '../ProductList/ProductList.css';
import './Seo.css';

const PAGE_LIMIT = 50;

const SeoCategoryList = ({ adminData }) => {
  const hasAccess = helpers.hasSeoAccess(adminData);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    searchQuery: '',
    seoScoreFilter: 'all',
    typeFilter: 'all',
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_LIMIT));

  const fetchCategories = useCallback(async (
    page = 1,
    search = '',
    seoScoreFilter = 'all',
    typeFilter = 'all'
  ) => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await seoAPI.fetchSeoCategories({
        page,
        limit: PAGE_LIMIT,
        searchQuery: search,
        seoScoreFilter,
        typeFilter,
      });

      if (response.success) {
        setItems(response.data?.items || []);
        setTotalCount(response.data?.totalCount || 0);
      } else {
        setError(response.msg || response.error || 'Failed to fetch category SEO list');
      }
    } catch (err) {
      console.error('SEO category list fetch error:', err);
      const message = err?.message || '';
      if (message.includes('JSON') || message.includes('Unexpected token')) {
        setError(
          'Category SEO API is unavailable. Restart the backend server to load the latest SEO routes.'
        );
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [hasAccess]);

  useEffect(() => {
    fetchCategories(
      currentPage,
      filters.searchQuery,
      filters.seoScoreFilter,
      filters.typeFilter
    );
  }, [currentPage, fetchCategories]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchCategories(1, filters.searchQuery, filters.seoScoreFilter, filters.typeFilter);
  };

  const handleEditSeo = (item) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleSeoSaved = (updatedItem) => {
    setItems((prev) =>
      prev.map((item) =>
        item._id === updatedItem._id && item.subCategoryId === updatedItem.subCategoryId
          ? { ...item, ...updatedItem }
          : item
      )
    );
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <>
      <div className="filter-section">
        <div className="filter-form">
          <div className="filter-group">
            <label>Type</label>
            <select
              value={filters.typeFilter}
              onChange={(e) => handleFilterChange('typeFilter', e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="category">Categories</option>
              <option value="subcategory">Subcategories</option>
            </select>
          </div>

          <div className="filter-group">
            <label>SEO Score</label>
            <select
              value={filters.seoScoreFilter}
              onChange={(e) => handleFilterChange('seoScoreFilter', e.target.value)}
              className="filter-select"
            >
              {SEO_SCORE_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by category or subcategory..."
              value={filters.searchQuery}
              onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
              className="filter-input"
            />
          </div>

          <button type="button" onClick={applyFilters} className="apply-filters-button">
            Apply Filters
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading categories...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
          <button
            type="button"
            className="retry-button"
            onClick={() =>
              fetchCategories(
                currentPage,
                filters.searchQuery,
                filters.seoScoreFilter,
                filters.typeFilter
              )
            }
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="products-table-container">
            <div className="table-wrapper">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Name</th>
                    <th>Parent Category</th>
                    <th>SEO Status</th>
                    <th>SEO Score</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="seo-empty-row">No categories found</td>
                    </tr>
                  ) : (
                    items.map((item) => {
                      const seoStatus = getCategorySeoStatus(item);
                      const seoScore = getCategorySeoScore(item);
                      const rowKey = `${item._id}-${item.subCategoryId || 'category'}`;

                      return (
                        <tr key={rowKey}>
                          <td>{item.type === 'subcategory' ? 'Subcategory' : 'Category'}</td>
                          <td>{item.title}</td>
                          <td>{item.parentCategory || '-'}</td>
                          <td>
                            <span className={`seo-status-badge ${getSeoStatusClass(seoStatus)}`}>
                              {seoStatus}
                            </span>
                          </td>
                          <td>{seoScore}/100</td>
                          <td>
                            <button
                              type="button"
                              className="seo-edit-button"
                              onClick={() => handleEditSeo(item)}
                            >
                              Edit SEO
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                Previous
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showEditModal && selectedItem && (
        <EditCategorySeoModal
          item={selectedItem}
          adminData={adminData}
          onClose={() => {
            setShowEditModal(false);
            setSelectedItem(null);
          }}
          onSaved={handleSeoSaved}
        />
      )}
    </>
  );
};

export default SeoCategoryList;
